import type { Catchment, CatchmentSchool, FinancialLayer } from "./types";

// ── Financial estimation (DEMO) ──────────────────────────────────────────────
// In production these would come from school_financial_metrics / catchment_financial_metrics
// For demo, we estimate from student_count using realistic per-student annual fee assumptions.

const ANNUAL_FEE_PER_STUDENT: Record<string, number> = {
  nursery: 350000,
  primary: 280000,
  secondary: 650000,
  tertiary: 1200000,
  vocational: 800000,
  other: 400000,
};

const DEFAULT_COLLECTION_RATE = 0.82;
const DEFAULT_BRECCS_PENETRATION = 0.45;

export interface SchoolFinance {
  annualFees: number;
  collected: number;
  outstanding: number;
  collectionRate: number;
  breccsVolume: number;
  breccsPenetration: number;
}

export interface CatchmentFinance {
  annualFees: number;
  collected: number;
  outstanding: number;
  collectionRate: number;
  breccsVolume: number;
  breccsPenetration: number;
  unservedSchools: number;
  unservedStudents: number;
  addressableVolume: number;
  opportunity: number;
}

export function getSchoolFinance(s: CatchmentSchool): SchoolFinance {
  const fee = ANNUAL_FEE_PER_STUDENT[s.education_level ?? "other"] ?? ANNUAL_FEE_PER_STUDENT.other;
  const annualFees = s.student_count * fee;
  // Deterministic pseudo-random collection rate based on school id hash
  const hash = s.id.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const collectionRate = Math.max(0.55, Math.min(0.98, DEFAULT_COLLECTION_RATE + ((hash % 20) - 10) / 100));
  const collected = Math.round(annualFees * collectionRate);
  const outstanding = annualFees - collected;
  const breccsPenetration = Math.max(0.1, Math.min(0.9, DEFAULT_BRECCS_PENETRATION + ((hash % 30) - 15) / 100));
  const breccsVolume = Math.round(collected * breccsPenetration);
  return { annualFees, collected, outstanding, collectionRate, breccsVolume, breccsPenetration };
}

export function getCatchmentFinance(schools: CatchmentSchool[]): CatchmentFinance {
  let annualFees = 0, collected = 0, breccsVolume = 0;
  let unservedSchools = 0, unservedStudents = 0, addressableVolume = 0;

  for (const s of schools) {
    const f = getSchoolFinance(s);
    annualFees += f.annualFees;
    collected += f.collected;
    breccsVolume += f.breccsVolume;

    // DEMO: ~40% of schools not yet active on BRECCS
    const hash = s.id.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
    if (hash % 10 < 4) {
      unservedSchools++;
      unservedStudents += s.student_count;
      addressableVolume += f.annualFees;
    }
  }

  const outstanding = annualFees - collected;
  const collectionRate = annualFees > 0 ? collected / annualFees : 0;
  const breccsPenetration = collected > 0 ? breccsVolume / collected : 0;
  const opportunity = addressableVolume;

  return {
    annualFees, collected, outstanding, collectionRate,
    breccsVolume, breccsPenetration,
    unservedSchools, unservedStudents, addressableVolume, opportunity,
  };
}

export function getCatchmentHealthScore(schools: CatchmentSchool[], finance: CatchmentFinance): number {
  if (schools.length === 0) return 0;
  const gpsVerified = schools.filter(s => s.location_status === "verified").length;
  const coverageScore = (gpsVerified / schools.length) * 25;
  const collectionScore = finance.collectionRate * 25;
  const penetrationScore = finance.breccsPenetration * 25;
  const metadataScore = Math.min(25, (schools.filter(s => s.education_level && s.ownership).length / schools.length) * 25);
  return Math.round(coverageScore + collectionScore + penetrationScore + metadataScore);
}

export function getDataQualityScore(schools: CatchmentSchool[]): number {
  if (schools.length === 0) return 0;
  let score = 0;
  const hasGps = schools.filter(s => s.latitude != null && s.longitude != null).length;
  score += (hasGps / schools.length) * 30;
  const hasCatchment = schools.filter(s => s.catchment_id).length;
  score += (hasCatchment / schools.length) * 25;
  const verified = schools.filter(s => s.location_status === "verified").length;
  score += (verified / schools.length) * 25;
  const hasMetadata = schools.filter(s => s.education_level && s.ownership).length;
  score += (hasMetadata / schools.length) * 20;
  return Math.round(score);
}

export function getInfrastructureScore(schools: CatchmentSchool[]): Record<string, number> {
  if (schools.length === 0) return { water: 0, electricity: 0, ict: 0, sanitation: 0, classroom: 0 };
  // DEMO: deterministic pseudo-random infrastructure scores
  const hash = schools.length;
  return {
    water: 60 + (hash * 7) % 35,
    electricity: 50 + (hash * 11) % 40,
    ict: 35 + (hash * 13) % 45,
    sanitation: 65 + (hash * 5) % 30,
    classroom: 55 + (hash * 9) % 35,
  };
}

// ── Financial layer color mapping ─────────────────────────────────────────────
export function getFinancialColor(value: number, max: number, layer: FinancialLayer): string {
  const ratio = max > 0 ? value / max : 0;
  if (layer === "outstanding") {
    // Higher = more red (bad)
    if (ratio > 0.7) return "#dc2626";
    if (ratio > 0.4) return "#ea580c";
    if (ratio > 0.2) return "#d97706";
    return "#65a30d";
  }
  if (layer === "collections") {
    // Higher = more green (good)
    if (ratio > 0.7) return "#059669";
    if (ratio > 0.4) return "#0891b2";
    if (ratio > 0.2) return "#0284c7";
    return "#64748b";
  }
  if (layer === "penetration") {
    if (ratio > 0.7) return "#7c3aed";
    if (ratio > 0.4) return "#6366f1";
    if (ratio > 0.2) return "#0284c7";
    return "#94a3b8";
  }
  if (layer === "opportunity") {
    if (ratio > 0.7) return "#d97706";
    if (ratio > 0.4) return "#f59e0b";
    if (ratio > 0.2) return "#fbbf24";
    return "#94a3b8";
  }
  // volume / population - blue scale
  if (ratio > 0.7) return "#0c3320";
  if (ratio > 0.4) return "#1e7a45";
  if (ratio > 0.2) return "#0284c7";
  return "#94a3b8";
}

// ── Formatting ───────────────────────────────────────────────────────────────
export function fmtUSD(n: number): string {
  if (n >= 1_000_000_000) return `USD ${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `USD ${(n / 1_000_000).toFixed(0)}M`;
  if (n >= 1_000) return `USD ${(n / 1_000).toFixed(0)}K`;
  return `USD ${n}`;
}

export function fmtNum(n: number): string {
  return new Intl.NumberFormat("en-ZW").format(n || 0);
}

export function fmtPct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

export function fmtArea(km2: number | null): string {
  if (!km2) return "—";
  return `${fmtNum(Math.round(km2))} km²`;
}

// ── Monthly trend data (DEMO) ────────────────────────────────────────────────
export function getMonthlyTrend(finance: CatchmentFinance): { month: string; billed: number; collected: number; outstanding: number; breccs: number }[] {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return months.map((month, i) => {
    const factor = 0.7 + (i / 12) * 0.4; // gradual growth
    const seasonal = i >= 0 && i <= 2 ? 1.2 : i >= 8 && i <= 10 ? 1.15 : 0.85; // term start peaks
    const billed = Math.round((finance.annualFees / 12) * factor * seasonal);
    const collected = Math.round(billed * finance.collectionRate);
    return {
      month,
      billed,
      collected,
      outstanding: billed - collected,
      breccs: Math.round(collected * finance.breccsPenetration),
    };
  });
}
